import React from 'react';
import { Paper, Typography, Box, CircularProgress, useTheme, alpha } from '@mui/material';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';
import { format, parseISO } from 'date-fns';

const formatDate = (d) => {
  try { return format(parseISO(d), 'MMM d'); } catch { return d; }
};

const formatDateFull = (d) => {
  try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return d; }
};

function CustomTooltip({ active, payload, label, valueLabel }) {
  const theme = useTheme();
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      bgcolor: 'background.paper',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      px: 2,
      py: 1,
      boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
    }}>
      <Typography variant="caption" color="text.secondary">{formatDateFull(label)}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color: payload[0]?.color }}>
        {payload[0]?.value?.toLocaleString()} {valueLabel}
      </Typography>
    </Box>
  );
}

function ChartCard({ title, data, loading, error, dataKey, color, gradientId, valueLabel }) {
  const theme = useTheme();

  return (
    <Paper elevation={0} sx={{
      p: 3,
      borderRadius: 3,
      border: `1px solid ${theme.palette.divider}`,
      height: '100%',
      minHeight: 300,
      display: 'flex',
      flexDirection: 'column',
      transition: 'box-shadow 0.2s ease',
      '&:hover': { boxShadow: `0 4px 20px ${alpha(color, 0.1)}` },
    }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 2 }}>
        {title}
      </Typography>
      {loading ? (
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress size={28} sx={{ color }} />
        </Box>
      ) : error ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                axisLine={false}
                tickLine={false}
                width={40}
              />
              <Tooltip content={<CustomTooltip valueLabel={valueLabel} />} />
              <Area
                type="monotone"
                dataKey={dataKey}
                stroke={color}
                strokeWidth={2.5}
                fill={`url(#${gradientId})`}
                dot={false}
                activeDot={{ r: 5, strokeWidth: 2, fill: '#fff', stroke: color }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
}

export function MiniChatsChart({ data, loading, error }) {
  const theme = useTheme();
  return (
    <ChartCard
      title="Chats (Month to Date)"
      data={data}
      loading={loading}
      error={error}
      dataKey="count"
      color={theme.palette.primary.main}
      gradientId="chatsGradient"
      valueLabel="chats"
    />
  );
}

export function MiniMessagesChart({ data, loading, error }) {
  const theme = useTheme();
  return (
    <ChartCard
      title="Messages (Month to Date)"
      data={data}
      loading={loading}
      error={error}
      dataKey="count"
      color={theme.palette.secondary.main || '#9c27b0'}
      gradientId="messagesGradient"
      valueLabel="messages"
    />
  );
}
